import React from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'hooks';

import { CheckIcon } from './icons';
import classes from './styles.module.scss';
import { PasswordConditionsType } from '../interface';

interface Props {
  score: number | undefined;
  conditionsStatus: PasswordConditionsType;
  value: string;
  className?: string;
}

const PasswordCriteriaPopUp = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { activeTheme } = useTheme();
  const { score = 0, conditionsStatus, value = '', className = '' } = props;
  const scoreLine = new Array(4).fill('');

  const getPasswordStrength = (scoreValue: number = 0) => {
    if (!scoreValue) return 1;
    return scoreValue;
  };

  const getScoreClass = (scoreValue: number): [string, string] => {
    if (scoreValue === null || scoreValue === undefined) return ['', ''];
    if (scoreValue <= 1) return [classes.weak, classes.weakText];
    if (scoreValue === 2) return [classes.average, classes.averageText];
    return [classes.strong, classes.strongText];
  };

  const getStrengthText = (scoreValue: number): string => {
    if (scoreValue === null || scoreValue === undefined) return '';
    if (scoreValue <= 1) return t('common:passwordStatus.weak');
    if (scoreValue === 2) return t('common:passwordStatus.average');
    if (scoreValue === 3) return t('common:passwordStatus.good');
    return t('common:passwordStatus.strong');
  };

  const strength = getPasswordStrength(score);

  return (
    (<div
      className={cx(classes.popUpContainer, {
        [className]: className,
      })}
      data-palette="PasswordCriteriaPopUp">
      <div className={classes.upperPart}>
        <div className={classes.checkItemHeader}>
          {value.length < 6 ? (
            <span data-palette="PasswordCriteriaPopUp">{t('auth:passwordCriteria.mustHave', { minCharacters: 6 })}</span>
          ) : (
            <span
              className={cx(classes.scoreText, getScoreClass(score)[1])}
              data-palette="PasswordCriteriaPopUp">
              {getStrengthText(score)}
            </span>
          )}
        </div>
        <div className={classes.pwdMeter}>
          {scoreLine.map((_, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={cx(classes.scoreLine, {
                [classes.lightScoreLine]: activeTheme === 'light',
                [getScoreClass(score)[0]]: index + 1 <= strength,
              })}
              data-palette="PasswordCriteriaPopUp" />
          ))}
        </div>
      </div>
      <div className={classes.lowerPart}>
        <div className={classes.checkItemHeader}>
          <span>{t('auth:passwordCriteria.toStrengthenInclude')}</span>
        </div>
        <div className={classes.checkItem}>
          <CheckIcon
            className={cx({
              [classes.unsatisfiedLight]: activeTheme === 'light',
              [classes.satisfied]: conditionsStatus.uppercaseLetter,
            })}
          />
          <span
            className={cx(classes.unsatisfiedText, {
              [classes.satisfiedText]: conditionsStatus.uppercaseLetter,
            })}
          >
            {t('auth:passwordCriteria.uppercaseLetter')}
          </span>
        </div>
        <div className={classes.checkItem}>
          <CheckIcon
            className={cx({
              [classes.unsatisfiedLight]: activeTheme === 'light',
              [classes.satisfied]: conditionsStatus.lowercaseLetter,
            })}
          />
          <span
            className={cx(classes.unsatisfiedText, {
              [classes.satisfiedText]: conditionsStatus.lowercaseLetter,
            })}
          >
            {t('auth:passwordCriteria.lowercaseLetter')}
          </span>
        </div>
        <div className={classes.checkItem}>
          <CheckIcon
            className={cx({
              [classes.unsatisfiedLight]: activeTheme === 'light',
              [classes.satisfied]: conditionsStatus.specialCharacter,
            })}
          />
          <span
            className={cx(classes.unsatisfiedText, {
              [classes.satisfiedText]: conditionsStatus.specialCharacter,
            })}
          >
            {t('auth:passwordCriteria.specialCharacter')}
          </span>
        </div>
        <div className={classes.checkItem}>
          <CheckIcon
            className={cx({
              [classes.unsatisfiedLight]: activeTheme === 'light',
              [classes.satisfied]: conditionsStatus.number,
            })}
          />
          <span
            className={cx(classes.unsatisfiedText, {
              [classes.satisfiedText]: conditionsStatus.number,
            })}
          >
            {t('auth:passwordCriteria.number')}
          </span>
        </div>
      </div>
    </div>)
  );
};

export default PasswordCriteriaPopUp;
